import {Button, Paper} from '@material-ui/core';
import {Alert, AlertTitle} from '@material-ui/lab';
import {Lens} from 'monocle-ts';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {changeWithLens} from 'Shared/utilities/changeWithLens';
import {saveNameInformation} from 'Users/ducks/userAccount';
import {
  eqNameInformation,
  InvalidNameInformation,
  NameInformation,
  SavingNameInformation,
  UnchangedNameInformation,
  ValidNameInformation,
} from 'Users/models/NameInformation';
import { changeAnalyticsState } from 'Core/ducks/analytics';
import { ANALYTICS_EVENT_ACTION } from 'Core/components/Analytics/AnalyticsEventConstants';

export type UserInformationSectionProps = {
  userId: string;
  nameInformation: NameInformation;
  currentNameInformation: NameInformation;
  onChange: (nameInfo: NameInformation) => void;
  saveNameInformation: typeof saveNameInformation;
} & WithSelectedAgencyProps;

const NameInformationLens = Lens.fromProp<NameInformation>();
const UserInformationSection = (props: UserInformationSectionProps) => {
  const dispatch = useDispatch();
  const {selectedAgency} = props;
  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  useEffect(() => {
    props.onChange(props.currentNameInformation);
  }, []);
  const isEditing = !(props.nameInformation instanceof UnchangedNameInformation);

  const hasNameInformationChanged = eqNameInformation.equals(props.nameInformation, props.currentNameInformation);
  const changeValue = changeWithLens(NameInformationLens, props.onChange, props.nameInformation);

  const userInfoSection = (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>User Information</h3>
      </div>
      {isEditing ? (
        <Alert severity="info" style={{marginBottom: '1.5rem', fontSize: '.95rem'}}>
          <AlertTitle>The following special characters are allowed: </AlertTitle>
          <strong>Underscore, Dash, Period and the At Symbol ( _ - . @ )</strong>
        </Alert>
      ) : (
        <></>
      )}
      <div className="user-info-section__name-fields">
        <RequiredTextInput
          className="user-info-section__profile-input"
          label="First Name"
          disabled={!isEditing}
          onChange={changeValue('firstName')}
          value={props.nameInformation.firstName}
          limitSpecialChars={true}
        />
        <RequiredTextInput
          className="user-info-section__profile-input"
          label="Last Name"
          disabled={!isEditing}
          onChange={changeValue('lastName')}
          value={props.nameInformation.lastName}
          limitSpecialChars={true}
        />
      </div>
      <div className="user-info-section__actions-container">
        {isEditing ? (
          <>
            <Button
              variant="contained"
              className="user-info-section__cancel-user-information-button"
              onClick={() => {
                props.onChange(props.currentNameInformation);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={hasNameInformationChanged}
              color="primary"
              variant="contained"
              className="user-info-section__save-user-information-button"
              onClick={() =>
                dispatch(
                  props.saveNameInformation(
                    selectedAgency.isNewYork,
                    props.userId,
                    props.nameInformation as ValidNameInformation,
                    dispatch,
                    selectedAgency.maskedIdentifier,
                    selectedAgency?.encryptedIdentifier,
                  )
                )
              }
            >
              Save
            </Button>
          </>
        ) : (
          <Button
            className="user-info-section__edit-button"
            aria-label="User Information Edit"
            color="primary"
            variant="contained"
            onClick={() => {
              dispatch(changeAnalyticsState({page: analytics.page, ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.USER_PROFILE_EDITUSERINFO}}))
              props.onChange(
                new InvalidNameInformation(props.nameInformation.firstName, props.nameInformation.lastName)
              )
            }}
          >
            Edit&nbsp;&nbsp;
            <i className="fa fa-edit" />
          </Button>
        )}
      </div>
    </Paper>
  );

  return props.nameInformation instanceof SavingNameInformation ? (
    <Paper className="user-profile__user-info-section">
      <Loading />
    </Paper>
  ) : (
    userInfoSection
  );
};

export default withSelectedAgency(UserInformationSection);
